import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { APP_VERSION } from '../version';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { useWeather, WeatherBadge } from './WeatherBadge';
import { getTeamStatsUrl } from '../utils/teamLinks';
import { TeamStatsModal } from './TeamStatsModal';
import { GlobalCalendarView } from './GlobalCalendar';
import { FriendsFamilyPanel } from './FriendsFamilyPanel';

// Map WeatherAPI condition text to native emoji
const getWeatherEmoji = (label) => {
  if (!label) return '🌤';
  const l = label.toLowerCase();
  if (l.includes('thunder') || l.includes('storm')) return '⛈️';
  if (l.includes('heavy rain') || l.includes('torrential')) return '🌧️';
  if (l.includes('sleet') || l.includes('freezing')) return '🌨️';
  if (l.includes('snow') || l.includes('blizzard')) return '❄️';
  if (l.includes('rain') || l.includes('drizzle') || l.includes('shower')) return '🌦️';
  if (l.includes('fog') || l.includes('mist') || l.includes('haze')) return '🌫️';
  if (l.includes('overcast')) return '☁️';
  if (l.includes('cloudy') || l.includes('cloud')) return '⛅';
  if (l.includes('sunny') || l.includes('clear')) return '☀️';
  return '🌤';
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TicketASaurus logo URL (transparent)
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_teamtix-manager/artifacts/7dt7doo8_TicketASaurus.png';

// Registration Request Modal Component
const RegistrationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    emailConfirm: '',
    howFoundUs: '',
    eventsInterested: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [emailMatch, setEmailMatch] = useState(true);
  const [showExistingUserPrompt, setShowExistingUserPrompt] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);

  const handleEmailChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    
    // Check if emails match
    if (field === 'email' || field === 'emailConfirm') {
      setEmailMatch(newData.email === newData.emailConfirm || newData.emailConfirm === '');
    }
  };

  const handlePasswordResetRequest = async () => {
    setSendingPasswordReset(true);
    try {
      const response = await axios.post(`${API}/public/lost-password-request`, {
        name: formData.name,
        email: formData.email,
        last_known_password: '',
        additional_info: 'Requested via "Request Access" form — user already exists.'
      });
      if (response.data.success) {
        setMessage('✅ Password reset request sent! An admin will contact you soon.');
        setShowExistingUserPrompt(false);
        setTimeout(() => {
          onClose();
          setFormData({ name: '', email: '', emailConfirm: '', howFoundUs: '', eventsInterested: '' });
          setMessage('');
          setShowExistingUserPrompt(false);
        }, 3000);
      } else {
        setMessage(`❌ ${response.data.error || 'Failed to send request'}`);
      }
    } catch (err) {
      setMessage('❌ Failed to send password reset request. Please try again.');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate emails match
    if (formData.email !== formData.emailConfirm) {
      setMessage('❌ Email addresses do not match');
      return;
    }
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.eventsInterested) {
      setMessage('❌ Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    setMessage('');
    setShowExistingUserPrompt(false);
    
    try {
      const response = await axios.post(`${API}/public/register-request`, {
        name: formData.name,
        email: formData.email,
        how_found_us: formData.howFoundUs,
        events_interested: formData.eventsInterested
      });
      
      if (response.data.success) {
        setMessage('✅ Request sent! We\'ll be in touch soon.');
        setTimeout(() => {
          onClose();
          setFormData({ name: '', email: '', emailConfirm: '', howFoundUs: '', eventsInterested: '' });
          setMessage('');
        }, 2000);
      } else if (response.data.existing_user) {
        setShowExistingUserPrompt(true);
        setMessage('');
      } else {
        setMessage(`❌ ${response.data.error || 'Failed to send request'}`);
      }
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.detail || 'Failed to send request. Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border-2 border-cyan-400/70 text-white"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-white">📝 Request Access</DialogTitle>
          <DialogDescription className="text-gray-400">
            Fill out this form to request access to track your season tickets.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">Full Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="John Smith"
              className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">Email Address *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleEmailChange('email', e.target.value)}
              placeholder="you@example.com"
              className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">Confirm Email *</label>
            <Input
              type="email"
              value={formData.emailConfirm}
              onChange={(e) => handleEmailChange('emailConfirm', e.target.value)}
              placeholder="you@example.com"
              className={`bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 ${
                !emailMatch ? 'border-red-500' : ''
              }`}
              required
            />
            {!emailMatch && (
              <p className="text-red-400 text-xs mt-1">Email addresses do not match</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">How did you find us?</label>
            <Input
              value={formData.howFoundUs}
              onChange={(e) => setFormData({...formData, howFoundUs: e.target.value})}
              placeholder="Friend referral, Google search, etc."
              className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">What events would you like to track? *</label>
            <Textarea
              value={formData.eventsInterested}
              onChange={(e) => setFormData({...formData, eventsInterested: e.target.value})}
              placeholder="Dallas Mavericks, Dallas Stars, Dallas Cowboys, etc."
              className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 min-h-[80px]"
              required
            />
          </div>
          
          {showExistingUserPrompt && (
            <div className="bg-amber-900/50 border border-amber-500/50 rounded-lg p-4 space-y-3">
              <p className="text-amber-200 text-sm font-medium">
                This email is already registered as a user.
              </p>
              <p className="text-gray-300 text-sm">
                Would you like to request a password reset instead?
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="request-password-reset-btn"
                  disabled={sendingPasswordReset}
                  onClick={handlePasswordResetRequest}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-sm"
                >
                  {sendingPasswordReset ? 'Sending...' : 'Yes, Reset My Password'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowExistingUserPrompt(false); setMessage(''); }}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800 text-sm"
                >
                  No, Go Back
                </Button>
              </div>
            </div>
          )}
          
          {message && (
            <p className={`text-sm text-center py-2 px-3 rounded ${
              message.includes('✅') ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
            }`}>
              {message}
            </p>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !emailMatch}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? 'Sending...' : '📧 Send Request'}
            </Button>
          </div>
          
          <div className="text-center pt-2 border-t border-gray-700 mt-2">
            <button
              type="button"
              data-testid="already-a-user-btn"
              onClick={onClose}
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
            >
              Already a user? Sign in here
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Lost Password Modal Component
const LostPasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    lastKnownPassword: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email) {
      setMessage('❌ Please fill in your name and email');
      return;
    }
    
    setIsSubmitting(true);
    setMessage('');
    
    try {
      const response = await axios.post(`${API}/public/lost-password-request`, {
        name: formData.name,
        email: formData.email,
        last_known_password: formData.lastKnownPassword,
        additional_info: formData.additionalInfo
      });
      
      if (response.data.success) {
        setMessage('✅ Password reset request sent! An admin will contact you soon.');
        setTimeout(() => {
          onClose();
          setFormData({ name: '', email: '', lastKnownPassword: '', additionalInfo: '' });
          setMessage('');
        }, 3000);
      } else {
        setMessage(`❌ ${response.data.error || 'Failed to send request'}`);
      }
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.detail || 'Failed to send request. Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border-2 border-cyan-400/70 text-white"
        style={{
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-white">🔐 Password Reset Request</DialogTitle>
          <DialogDescription className="text-gray-400">
            Fill out this form and an administrator will help reset your password.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Your Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Enter your full name"
              className="bg-gray-800 border-gray-600 text-white"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email Address *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="Enter your email"
              className="bg-gray-800 border-gray-600 text-white"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Last Known Password</label>
            <Input
              type="text"
              value={formData.lastKnownPassword}
              onChange={(e) => setFormData({...formData, lastKnownPassword: e.target.value})}
              placeholder="What you remember (optional)"
              className="bg-gray-800 border-gray-600 text-white"
            />
            <p className="text-xs text-gray-500 mt-1">This helps verify your identity</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Additional Information</label>
            <Textarea
              value={formData.additionalInfo}
              onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
              placeholder="Any other details that might help identify your account..."
              className="bg-gray-800 border-gray-600 text-white min-h-[80px]"
            />
          </div>
          
          {message && (
            <p className={`text-sm text-center p-2 rounded ${message.includes('✅') ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
              {message}
            </p>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? 'Sending...' : '📧 Send Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};


export const CoverPage = ({ onLogin, seasonConfig, availableSeasons }) => {
  const [step, setStep] = useState(() => {
    // If emulating, start at step 2 (event selection)
    const isEmulating = localStorage.getItem('isEmulating') === 'true';
    if (isEmulating) {
      return 2;
    }
    return 1;
  }); // Step 1: Login, Step 1.5: Biometric Setup, Step 2: Select Event, Step 3: Global Calendar
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(() => {
    // If emulating, set user from localStorage
    const isEmulating = localStorage.getItem('isEmulating') === 'true';
    if (isEmulating) {
      return {
        email: localStorage.getItem('emulatedUserEmail'),
        name: localStorage.getItem('emulatedUserName'),
        partner_id: localStorage.getItem('emulatedUserPartnerId'),
        is_event_manager: localStorage.getItem('emulatedUserIsEventManager') === 'true'
      };
    }
    return null;
  });
  const [userEvents, setUserEvents] = useState(() => {
    // If emulating, try to restore from sessionStorage
    const isEmulating = localStorage.getItem('isEmulating') === 'true';
    if (isEmulating) {
      const cached = sessionStorage.getItem('emulatedUserEvents');
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {
          console.error('Error parsing cached events:', e);
        }
      }
    }
    return [];
  });
  const [ipAddress, setIpAddress] = useState('Loading...');
  const [loadedSeasons, setLoadedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [rememberEmail, setRememberEmail] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricNeverAsk, setBiometricNeverAsk] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(() => {
    const width = window.innerWidth;
    return width >= 768 && width < 1024;
  });
  // Also detect larger tablets (iPad Pro, landscape tablets) - anything between 768 and 1366
  const [isLargeTablet, setIsLargeTablet] = useState(() => {
    const width = window.innerWidth;
    return width >= 768 && width <= 1366;
  });
  // For tablets (768px-1366px), default to 'mobile' view; otherwise null for auto-detection
  const [viewOverride, setViewOverride] = useState(() => {
    const width = window.innerWidth;
    const isTabletWidth = width >= 768 && width <= 1366;
    return isTabletWidth ? 'mobile' : null;
  });
  const [footerText, setFooterText] = useState('© 2025 TicketASaurus');
  const [showPassword, setShowPassword] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  // Season W/L records keyed by event.id — fetched for the panel badges on the event picker
  const [seasonRecords, setSeasonRecords] = useState({});

  // Fetch season W/L records for all user events (one lightweight call per event)
  useEffect(() => {
    const sportEvents = (userEvents || []).filter(e => {
      const s = (e.sport || '').toLowerCase();
      return s && s !== 'family' && s !== 'concerts';
    });
    if (sportEvents.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(sportEvents.map(async (ev) => {
          try {
            const r = await axios.get(`${API}/nba/results`, { params: { event_id: ev.id } });
            return [ev.id, r.data || null];
          } catch {
            return [ev.id, null];
          }
        }));
        if (cancelled) return;
        setSeasonRecords(prev => {
          const next = { ...prev };
          for (const [id, rec] of results) {
            if (rec) next[id] = rec;
          }
          return next;
        });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [userEvents]);

  // Auto-open register modal if ?register=true is in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === 'true') {
      setShowRegisterModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const [showLostPasswordModal, setShowLostPasswordModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showFriendsFamily, setShowFriendsFamily] = useState(false);
  const [profilePassword, setProfilePassword] = useState('');
  const [profileConfirmPassword, setProfileConfirmPassword] = useState('');
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [showHiddenEvents, setShowHiddenEvents] = useState(false);
  const [showWeatherPanel, setShowWeatherPanel] = useState(false);
  const [weatherRadarView, setWeatherRadarView] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [radarKey, setRadarKey] = useState(0);

  // Get user location for radar map
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserCoords({ lat: pos.coords.latitude.toFixed(2), lon: pos.coords.longitude.toFixed(2) }),
        () => {},
        { timeout: 5000 }
      );
    }
  }, []);
  
  // Check for emulation mode on initial component mount
  const isEmulatingMode = localStorage.getItem('isEmulating') === 'true';

  // Weather data for header icon and panel
  const weatherForecast = useWeather();
  const todayWeather = (() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return weatherForecast[todayStr] || null;
  })();
  const weatherDays = (() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    // Only show today + future days in the forecast panel —
    // backfilled past-day entries live in the cache for other UI (calendar overlays)
    // but shouldn't clutter the "14-day forecast" view.
    return Object.entries(weatherForecast)
      .filter(([d]) => d >= todayStr)
      .sort(([a], [b]) => a.localeCompare(b));
  })();

  // Check if user is switching events (soft logout) - restore session
  useEffect(() => {
    const checkSwitchingEvents = async () => {
      const switching = sessionStorage.getItem('switchingEvents');
      const storedUser = sessionStorage.getItem('loggedInUser');
      
      // Check for emulation mode
      const isEmulating = localStorage.getItem('isEmulating') === 'true';
      if (isEmulating) {
        const emulatedEmail = localStorage.getItem('emulatedUserEmail');
        const emulatedName = localStorage.getItem('emulatedUserName');
        const emulatedPartnerId = localStorage.getItem('emulatedUserPartnerId');
        const emulatedIsEventManager = localStorage.getItem('emulatedUserIsEventManager') === 'true';
        
        // Wait for seasons to load before proceeding
        if (loadingSeasons) {
          // Still loading, don't set checkingSession to false yet
          return;
        }
        
        // Clear switching flag for emulation mode
        sessionStorage.removeItem('switchingEvents');
        
        if (emulatedEmail && loadedSeasons.length > 0) {
          const emulatedUser = {
            email: emulatedEmail,
            name: emulatedName,
            partner_id: emulatedPartnerId,
            is_event_manager: emulatedIsEventManager
          };
          
          // Find events this user is assigned to
          const assignedEvents = loadedSeasons.filter(event => 
            event.assigned_users?.includes(emulatedEmail.toLowerCase()) ||
            event.assigned_users?.includes(emulatedEmail)
          );
          
          if (assignedEvents.length > 0) {
            setLoggedInUser(emulatedUser);
            setUserEvents(assignedEvents);
            sessionStorage.setItem('loggedInUser', JSON.stringify(emulatedUser));
            // Mark as authenticated
            localStorage.setItem('isAuthenticated', 'true');
            setStep(2);
          } else {
            // User has no assigned events - show all events they might have access to
            console.log('Emulated user has no assigned events, showing all available events');
            setLoggedInUser(emulatedUser);
            setUserEvents(loadedSeasons);
            sessionStorage.setItem('loggedInUser', JSON.stringify(emulatedUser));
            localStorage.setItem('isAuthenticated', 'true');
            setStep(2);
          }
        }
        setCheckingSession(false);
        return;
      }
      
      if (switching === 'true' && storedUser) {
        try {
          const user = JSON.parse(storedUser);
          sessionStorage.removeItem('switchingEvents');
          
          // Wait for events to load
          if (!loadingSeasons && loadedSeasons.length > 0) {
            // Find events this user is assigned to
            const assignedEvents = loadedSeasons.filter(event => 
              event.assigned_users?.includes(user.email.toLowerCase()) ||
              event.assigned_users?.includes(user.email)
            );
            
            if (assignedEvents.length > 0) {
              setLoggedInUser(user);
              setUserEvents(assignedEvents);
              setStep(2);
            }
          }
        } catch (e) {
          console.error('Error restoring session:', e);
        }
      }
      setCheckingSession(false);
    };
    
    if (!loadingSeasons) {
      checkSwitchingEvents();
    }
  }, [loadingSeasons, loadedSeasons]);

  // Set user events for emulation mode once seasons load
  useEffect(() => {
    const isEmulating = localStorage.getItem('isEmulating') === 'true';
    if (isEmulating && !loadingSeasons && loadedSeasons.length > 0 && userEvents.length === 0) {
      const emulatedEmail = localStorage.getItem('emulatedUserEmail');
      if (emulatedEmail) {
        // Check if we have cached events in sessionStorage
        const cachedEvents = sessionStorage.getItem('emulatedUserEvents');
        if (cachedEvents) {
          try {
            const events = JSON.parse(cachedEvents);
            setUserEvents(events);
            return;
          } catch (e) {
            console.error('Error parsing cached events:', e);
          }
        }
        
        // Find events this user is assigned to
        const assignedEvents = loadedSeasons.filter(event => 
          event.assigned_users?.includes(emulatedEmail.toLowerCase()) ||
          event.assigned_users?.includes(emulatedEmail)
        );
        
        if (assignedEvents.length > 0) {
          setUserEvents(assignedEvents);
          sessionStorage.setItem('emulatedUserEvents', JSON.stringify(assignedEvents));
        } else {
          // Show all events if user has no specific assignments
          setUserEvents(loadedSeasons);
          sessionStorage.setItem('emulatedUserEvents', JSON.stringify(loadedSeasons));
        }
      }
    }
  }, [loadingSeasons, loadedSeasons, userEvents.length]);

  // Biometric data helpers (defined outside useEffect)
  const getBiometricData = () => {
    try {
      const data = localStorage.getItem('biometric_data');
      return data ? JSON.parse(data) : {};
    } catch (e) {
      return {};
    }
  };
  
  const saveBiometricData = (email, userData) => {
    try {
      const data = getBiometricData();
      data[email] = { ...data[email], ...userData };
      localStorage.setItem('biometric_data', JSON.stringify(data));
      localStorage.setItem('last_biometric_email', email);
    } catch (e) {
      console.error('Error saving biometric data:', e);
    }
  };


  // Check if mobile/tablet and biometrics available
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const userAgentMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      // iPadOS 13+ reports a desktop Mac User-Agent, so we also detect a touch-
      // capable "Mac" as an iPad. This is the standard workaround.
      const isIpadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
      const viewportMobile = width <= 400;
      setIsMobile(userAgentMobile || viewportMobile || isIpadOS);
      setIsTablet(width >= 768 && width < 1024);
      setIsLargeTablet(width >= 768 && width <= 1366);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    // Check for stored email
    const storedEmail = localStorage.getItem('rememberedEmail');
    if (storedEmail) {
      setEmail(storedEmail);
      setRememberEmail(true);
    }

    // Check if biometric is available and enabled
    const checkBiometric = async () => {
      try {
        const dataStr = localStorage.getItem('biometric_data');
        const data = dataStr ? JSON.parse(dataStr) : {};
        const lastEmail = localStorage.getItem('last_biometric_email');
        
        let enabled = false;
        let hasCredential = false;
        let neverAsk = false;
        
        if (lastEmail && data[lastEmail]) {
          enabled = data[lastEmail].enabled === true;
          hasCredential = !!data[lastEmail].credentialId;
          neverAsk = data[lastEmail].neverAsk === true;
        }
        
        setBiometricEnabled(enabled && hasCredential);
        setBiometricNeverAsk(neverAsk);
        
        if (enabled && hasCredential) {
          setBiometricAvailable(true);
        } else if (window.PublicKeyCredential) {
          try {
            const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            setBiometricAvailable(available);
          } catch (e) {
            setBiometricAvailable(window.innerWidth < 400);
          }
        }
      } catch (e) {
        console.error('Error checking biometric:', e);
      }
    };
    checkBiometric();

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fetch global footer text
  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const response = await axios.get(`${API}/public/footer`);
        if (response.data.success && response.data.footer_text) {
          setFooterText(response.data.footer_text);
        }
      } catch (err) {
        console.log('Using default footer');
      }
    };
    fetchFooter();
  }, []);

  // Fetch IP address
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setIpAddress(data.ip);
      } catch (err) {
        setIpAddress('Unable to fetch');
      }
    };
    fetchIP();
  }, []);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events/`);
        if (response.data.success) {
          const activeEvents = response.data.events.filter(e => e.status === 'active');
          setLoadedSeasons(activeEvents.map(event => ({
            id: event.id,
            teamName: event.name,
            sport: event.sport || 'basketball',
            season: event.season || '2025-2026',
            primaryColor: event.primary_color || '#0053BC',
            secondaryColor: event.secondary_color || '#00285E',
            assigned_users: event.assigned_users || [],
            is_hidden: event.is_hidden || false
          })));
        }
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoadingSeasons(false);
      }
    };
    fetchEvents();
  }, []);

  // Handle login submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    // Save or clear remembered email
    if (rememberEmail) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    
    try {
      const response = await axios.post(`${API}/users/login`, {
        email: email.trim().toLowerCase(),
        password
      });
      
      if (response.data.success) {
        const user = response.data.user;
        
        // Store user for session (for switching events)
        sessionStorage.setItem('loggedInUser', JSON.stringify(user));
        
        // Store for biometric
        localStorage.setItem('last_biometric_email', email.toLowerCase());
        
        // Check if super admin - redirect directly
        if (user.role === 'superadmin') {
          sessionStorage.setItem('superadmin_user', JSON.stringify(user));
          window.location.href = '/superadmin';
          return;
        }
        
        // Find events this user is assigned to
        // If loadedSeasons is empty (race condition), fetch events
        let eventsToFilter = loadedSeasons;
        if (eventsToFilter.length === 0) {
          try {
            const eventsRes = await axios.get(`${API}/events/`);
            if (eventsRes.data.success) {
              const activeEvents = eventsRes.data.events.filter(e => e.status === 'active');
              eventsToFilter = activeEvents.map(event => ({
                id: event.id,
                teamName: event.name,
                sport: event.sport || 'basketball',
                season: event.season || '2025-2026',
                primaryColor: event.primary_color || '#0053BC',
                secondaryColor: event.secondary_color || '#00285E',
                assigned_users: event.assigned_users || [],
                is_hidden: event.is_hidden || false
              }));
              setLoadedSeasons(eventsToFilter);
            }
          } catch (err) {
            console.error('Error fetching events:', err);
          }
        }
        
        const assignedEvents = eventsToFilter.filter(event => 
          event.assigned_users?.includes(user.email.toLowerCase()) ||
          event.assigned_users?.includes(user.email)
        );
        
        if (assignedEvents.length === 0) {
          setError('You are not assigned to any events. Please contact an administrator.');
          setIsLoading(false);
          return;
        }
        
        // If only one event, go directly to it (but check biometric first)
        if (assignedEvents.length === 1) {
          // Check if we should show biometric prompt on ALL devices (desktop has Face ID, Touch ID too)
          const bioData = getBiometricData();
          const userBioData = bioData[user.email.toLowerCase()] || {};
          
          // Check if WebAuthn/biometrics is available on this device
          const webAuthnAvailable = window.PublicKeyCredential !== undefined;
          
          // Show biometric prompt if: WebAuthn available, not enabled, not "never ask", and either first time or every 3 skips
          const skipCount = userBioData.skipCount || 0;
          const shouldShowBiometric = webAuthnAvailable && 
            !userBioData.enabled && 
            !userBioData.neverAsk && 
            (skipCount === 0 || skipCount % 3 === 0);
          
          if (shouldShowBiometric) {
            // Show biometric prompt first, then go to single event
            setLoggedInUser(user);
            setUserEvents(assignedEvents);
            setShowBiometricPrompt(true);
            setStep(2); // Will show biometric overlay
          } else {
            const selectedEvent = {
              id: assignedEvents[0].id,
              name: assignedEvents[0].teamName,
              sport: assignedEvents[0].sport,
              season: assignedEvents[0].season,
              primaryColor: assignedEvents[0].primaryColor,
              secondaryColor: assignedEvents[0].secondaryColor,
              assigned_users: assignedEvents[0].assigned_users
            };
            await onLogin(null, false, user, selectedEvent);
          }
          return;
        }
        
        // Multiple events - check biometric prompt on ALL devices, then show selection
        const bioData = getBiometricData();
        const userBioData = bioData[user.email.toLowerCase()] || {};
        
        // Check if WebAuthn/biometrics is available on this device
        const webAuthnAvailable = window.PublicKeyCredential !== undefined;
        
        // Show biometric prompt if: WebAuthn available, not enabled, not "never ask", and either first time or every 3 skips
        const skipCount = userBioData.skipCount || 0;
        const shouldShowBiometric = webAuthnAvailable && 
          !userBioData.enabled && 
          !userBioData.neverAsk && 
          (skipCount === 0 || skipCount % 3 === 0);
        
        setLoggedInUser(user);
        setUserEvents(assignedEvents);
        if (shouldShowBiometric) {
          setShowBiometricPrompt(true);
        }
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  // Setup biometric (fingerprint)
  const setupBiometric = async () => {
    try {
      if (!loggedInUser?.email) {
        alert('User not logged in.');
        return;
      }

      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: challenge,
          rp: {
            name: "TicketASaurus",
            id: window.location.hostname,
          },
          user: {
            id: new Uint8Array(16),
            name: loggedInUser.email,
            displayName: loggedInUser.name || "User",
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },
            { alg: -257, type: "public-key" }
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required",
          },
          timeout: 60000,
          attestation: "none"
        }
      });

      if (credential) {
        const credId = credential.id;
        const rawIdBase64 = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        
        saveBiometricData(loggedInUser.email.toLowerCase(), {
          enabled: true,
          credentialId: credId,
          rawId: rawIdBase64,
          setupDate: new Date().toISOString()
        });
        
        setBiometricEnabled(true);
        setShowBiometricPrompt(false);
        alert('✅ Fingerprint enabled! You can now use fingerprint to login.');
        
        // If single event, go directly
        if (userEvents.length === 1) {
          const selectedEvent = {
            id: userEvents[0].id,
            name: userEvents[0].teamName,
            sport: userEvents[0].sport,
            season: userEvents[0].season,
            primaryColor: userEvents[0].primaryColor,
            secondaryColor: userEvents[0].secondaryColor,
            assigned_users: userEvents[0].assigned_users
          };
          await onLogin(null, false, loggedInUser, selectedEvent);
        }
      }
    } catch (err) {
      console.error('Biometric setup error:', err);
      alert('Could not set up fingerprint: ' + err.message);
    }
  };

  // Skip biometric setup (Maybe Later)
  const skipBiometric = () => {
    // Save "maybe later" with timestamp so we can remind them after 3 logins
    if (loggedInUser?.email) {
      const bioData = getBiometricData();
      const userData = bioData[loggedInUser.email.toLowerCase()] || {};
      const skipCount = (userData.skipCount || 0) + 1;
      saveBiometricData(loggedInUser.email.toLowerCase(), { 
        ...userData,
        skipCount,
        lastSkipped: new Date().toISOString()
      });
    }
    setShowBiometricPrompt(false);
    // If single event after skipping, go directly
    if (userEvents.length === 1) {
      const selectedEvent = {
        id: userEvents[0].id,
        name: userEvents[0].teamName,
        sport: userEvents[0].sport,
        season: userEvents[0].season,
        primaryColor: userEvents[0].primaryColor,
        secondaryColor: userEvents[0].secondaryColor,
        assigned_users: userEvents[0].assigned_users
      };
      onLogin(null, false, loggedInUser, selectedEvent);
    }
  };

  // Never ask for biometric again
  const neverAskBiometric = () => {
    if (loggedInUser?.email) {
      saveBiometricData(loggedInUser.email.toLowerCase(), { neverAsk: true });
    }
    setBiometricNeverAsk(true);
    setShowBiometricPrompt(false);
    // If single event after skipping, go directly
    if (userEvents.length === 1) {
      const selectedEvent = {
        id: userEvents[0].id,
        name: userEvents[0].teamName,
        sport: userEvents[0].sport,
        season: userEvents[0].season,
        primaryColor: userEvents[0].primaryColor,
        secondaryColor: userEvents[0].secondaryColor,
        assigned_users: userEvents[0].assigned_users
      };
      onLogin(null, false, loggedInUser, selectedEvent, viewOverride);
    }
  };

  // Handle biometric login
  const handleBiometricLogin = async () => {
    try {
      const dataStr = localStorage.getItem('biometric_data');
      const data = dataStr ? JSON.parse(dataStr) : {};
      const lastEmail = localStorage.getItem('last_biometric_email');
      
      if (!lastEmail || !data[lastEmail]) {
        setError('No biometric data found. Please login with password first.');
        return;
      }
      
      const userData = data[lastEmail];
      const { rawId } = userData;
      
      if (!rawId) {
        setError('Biometric credentials incomplete. Please set up fingerprint again.');
        return;
      }
      
      // Generate a challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Decode the raw ID
      let rawIdArray;
      try {
        rawIdArray = Uint8Array.from(atob(rawId), c => c.charCodeAt(0));
      } catch (decodeErr) {
        setError('Error with stored credentials. Please set up fingerprint again.');
        return;
      }

      // Request biometric authentication
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          allowCredentials: [{
            id: rawIdArray,
            type: 'public-key'
          }],
          timeout: 60000,
          userVerification: "preferred"
        }
      });

      if (assertion) {
        // Biometric verified - get user from API
        setIsLoading(true);
        const response = await axios.post(`${API}/users/biometric-login`, {
          email: lastEmail
        });
        
        if (response.data.success) {
          const user = response.data.user;
          
          // Store user for session (for switching events)
          sessionStorage.setItem('loggedInUser', JSON.stringify(user));
          
          // Check if super admin
          if (user.role === 'superadmin') {
            sessionStorage.setItem('superadmin_user', JSON.stringify(user));
            window.location.href = '/superadmin';
            return;
          }
          
          // Find events this user is assigned to
          const assignedEvents = loadedSeasons.filter(event => 
            event.assigned_users?.includes(user.email.toLowerCase()) ||
            event.assigned_users?.includes(user.email)
          );
          
          if (assignedEvents.length === 0) {
            setError('You are not assigned to any events.');
            setIsLoading(false);
            return;
          }
          
          // If only one event, go directly
          if (assignedEvents.length === 1) {
            const selectedEvent = {
              id: assignedEvents[0].id,
              name: assignedEvents[0].teamName,
              sport: assignedEvents[0].sport,
              season: assignedEvents[0].season,
              primaryColor: assignedEvents[0].primaryColor,
              secondaryColor: assignedEvents[0].secondaryColor,
              assigned_users: assignedEvents[0].assigned_users
            };
            await onLogin(null, false, user, selectedEvent);
            return;
          }
          
          // Multiple events - show selection
          setLoggedInUser(user);
          setUserEvents(assignedEvents);
          setStep(2);
        } else {
          setError('Login failed: ' + (response.data.error || 'Unknown error'));
        }
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Biometric login error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Fingerprint cancelled or not recognized. Try again or use password.');
      } else if (err.name === 'InvalidStateError') {
        setError('Fingerprint credential not found. Please reset and set up again.');
      } else {
        setError(`Error: ${err.message || err.name}. Try using password.`);
      }
      setIsLoading(false);
    }
  };

  // Handle event selection (Step 2)
  const handleSelectEvent = async (event) => {
    const selectedEvent = {
      id: event.id,
      name: event.teamName,
      sport: event.sport,
      season: event.season,
      primaryColor: event.primaryColor,
      secondaryColor: event.secondaryColor,
      assigned_users: event.assigned_users
    };
    await onLogin(null, false, loggedInUser, selectedEvent, viewOverride);
  };

  // Handle back to login
  const handleBack = () => {
    setStep(1);
    setLoggedInUser(null);
    setUserEvents([]);
    setError('');
  };

  // Handle full sign out (clear everything)
  const handleFullSignOut = () => {
    sessionStorage.removeItem('loggedInUser');
    sessionStorage.removeItem('switchingEvents');
    setStep(1);
    setLoggedInUser(null);
    setUserEvents([]);
    setPassword('');
    setError('');
  };

  // Toggle event hidden status
  const handleToggleEventHidden = async (event, e) => {
    e.stopPropagation(); // Prevent event selection when clicking toggle
    try {
      const response = await axios.patch(`${API}/events/${event.id}/toggle-hidden`);
      if (response.data.success) {
        // Update the event in userEvents state
        setUserEvents(prevEvents => 
          prevEvents.map(ev => 
            ev.id === event.id 
              ? { ...ev, is_hidden: response.data.is_hidden }
              : ev
          )
        );
        // Also update loadedSeasons
        setLoadedSeasons(prevSeasons =>
          prevSeasons.map(ev =>
            ev.id === event.id
              ? { ...ev, is_hidden: response.data.is_hidden }
              : ev
          )
        );
      }
    } catch (err) {
      console.error('Failed to toggle event hidden status:', err);
    }
  };

  // Filter events by hidden status
  const visibleEvents = userEvents.filter(event => !event.is_hidden);
  const hiddenEvents = userEvents.filter(event => event.is_hidden);

  // Get sport emoji
  const getSportEmoji = (sport) => {
    switch (sport?.toLowerCase()) {
      case 'hockey': return '🏒';
      case 'football': return '🏈';
      case 'basketball': return '🏀';
      case 'baseball': return '⚾';
      case 'concerts': return '🎭';
      case 'family': return '🏠';
      default: return '🎫';
    }
  };

  // Get team logo
  const getTeamLogo = (event) => {
    const sport = event.sport?.toLowerCase();
    if (sport === 'hockey') return '/dallas-stars-logo.svg';
    if (sport === 'football') return '/dallas-cowboys-logo.png';
    if (sport === 'basketball') return '/dallas-mavs-logo.png';
    if (sport === 'baseball') return '/texas-rangers-logo.png';
    if (sport === 'family') return '/favicon-ticketasaurus.svg';
    if (sport === 'concerts') return '/favicon-ticketasaurus.svg';
    return '/favicon-ticketasaurus.svg';
  };

  // Step 3: Global Calendar - renders full screen (must check before main content)
  if (step === 3) {
    // Determine the view mode to pass to GlobalCalendarView
    // For tablets (768px-1366px), respect the viewOverride toggle
    // Otherwise, use auto-detection (null)
    const calendarViewMode = isLargeTablet ? (viewOverride || 'mobile') : null;
    
    return (
      <GlobalCalendarView 
        events={userEvents} 
        onBack={() => setStep(2)}
        forceViewMode={calendarViewMode}
        userEmail={loggedInUser?.email}
        userPartnerId={loggedInUser?.partner_id}
      />
    );
  }

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden ${localStorage.getItem('isEmulating') === 'true' ? 'pt-16' : ''}`}
      style={{
        background: 'radial-gradient(ellipse at top, #1a0a2e 0%, #0a0a1a 50%, #0d0d1f 100%)'
      }}
    >
      {/* Emulation Mode Banner */}
      {localStorage.getItem('isEmulating') === 'true' && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-white py-2 px-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <span className="text-xl">👤</span>
            <span className="font-semibold">
              Emulating: {localStorage.getItem('emulatedUserName')} ({localStorage.getItem('emulatedUserEmail')})
            </span>
            {localStorage.getItem('emulatedUserPartnerId') && (
              <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
                Partner {localStorage.getItem('emulatedUserPartnerId')}
              </span>
            )}
          </div>
          <button
            onClick={() => {
              // Clear emulation data
              localStorage.removeItem('isEmulating');
              localStorage.removeItem('emulatedUserEmail');
              localStorage.removeItem('emulatedUserName');
              localStorage.removeItem('emulatedUserPartnerId');
              localStorage.removeItem('emulatedUserIsEventManager');
              localStorage.removeItem('currentEvent');
              localStorage.removeItem('isAuthenticated');
              sessionStorage.removeItem('emulatedUser');
              sessionStorage.removeItem('loggedInUser');
              sessionStorage.removeItem('switchingEvents');
              sessionStorage.removeItem('emulatedUserEvents');
              // Redirect to superadmin
              window.location.href = '/superadmin';
            }}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-lg font-medium transition-colors"
          >
            <span>✕</span>
            <span>Exit Emulation</span>
          </button>
        </div>
      )}
      
      {/* Epic Stadium Arena Background - Optimized (no animations) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        
        {/* Static spotlight beams from top */}
        <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/40 via-cyan-400/10 to-transparent transform -skew-x-12 blur-sm"></div>
        <div className="absolute top-0 left-1/3 w-2 h-full bg-gradient-to-b from-purple-500/30 via-purple-500/5 to-transparent transform skew-x-6 blur-md"></div>
        <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/35 via-cyan-400/10 to-transparent transform skew-x-12 blur-sm"></div>
        <div className="absolute top-0 right-1/3 w-2 h-full bg-gradient-to-b from-blue-500/25 via-blue-500/5 to-transparent transform -skew-x-6 blur-md"></div>
        <div className="absolute top-0 left-1/2 w-3 h-full bg-gradient-to-b from-white/20 via-cyan-300/10 to-transparent transform -translate-x-1/2 blur-lg"></div>
        
        {/* Static glowing orbs */}
        <div className="absolute top-[5%] left-[10%] w-32 h-32 bg-cyan-400/30 rounded-full blur-[60px]"></div>
        <div className="absolute top-[5%] right-[10%] w-32 h-32 bg-purple-500/30 rounded-full blur-[60px]"></div>
        <div className="absolute top-[3%] left-[30%] w-24 h-24 bg-blue-400/25 rounded-full blur-[50px]"></div>
        <div className="absolute top-[3%] right-[30%] w-24 h-24 bg-teal-400/25 rounded-full blur-[50px]"></div>
        <div className="absolute top-[8%] left-1/2 w-40 h-40 bg-cyan-300/20 rounded-full blur-[80px] transform -translate-x-1/2"></div>
        
        {/* Large ambient color zones */}
        <div className="absolute top-1/4 left-0 w-[50%] h-[60%] bg-gradient-to-r from-purple-600/15 via-purple-600/5 to-transparent blur-[100px]"></div>
        <div className="absolute top-1/4 right-0 w-[50%] h-[60%] bg-gradient-to-l from-cyan-500/15 via-cyan-500/5 to-transparent blur-[100px]"></div>
        <div className="absolute bottom-0 left-1/4 right-1/4 h-[40%] bg-gradient-to-t from-blue-600/10 via-blue-600/5 to-transparent blur-[80px]"></div>
        
        {/* Hexagon grid pattern */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2322d3ee' fill-opacity='0.4'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        {/* Diagonal speed lines */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 80px,
            rgba(139, 92, 246, 0.3) 80px,
            rgba(139, 92, 246, 0.3) 81px
          )`
        }}></div>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 120px,
            rgba(34, 211, 238, 0.3) 120px,
            rgba(34, 211, 238, 0.3) 121px
          )`
        }}></div>
        
        {/* Static bokeh lights */}
        <div className="absolute top-[20%] left-[15%] w-4 h-4 bg-cyan-400/50 rounded-full blur-[2px]"></div>
        <div className="absolute top-[35%] right-[20%] w-3 h-3 bg-purple-400/40 rounded-full blur-[2px]"></div>
        <div className="absolute top-[50%] left-[8%] w-2 h-2 bg-blue-400/40 rounded-full blur-[1px]"></div>
        <div className="absolute top-[60%] right-[12%] w-3 h-3 bg-teal-400/30 rounded-full blur-[2px]"></div>
        <div className="absolute top-[25%] right-[35%] w-2 h-2 bg-pink-400/30 rounded-full blur-[1px]"></div>
        <div className="absolute top-[70%] left-[25%] w-2 h-2 bg-cyan-300/40 rounded-full blur-[1px]"></div>
        
        {/* Glowing ticket stubs */}
        <svg className="absolute top-[12%] left-[5%] w-32 h-20 opacity-20 transform -rotate-12" viewBox="0 0 120 80" fill="none">
          <rect x="5" y="5" width="110" height="70" rx="8" stroke="url(#ticketGrad1)" strokeWidth="2"/>
          <line x1="85" y1="5" x2="85" y2="75" stroke="url(#ticketGrad1)" strokeWidth="1.5" strokeDasharray="4 4"/>
          <circle cx="85" cy="5" r="5" fill="#0a0a1a"/>
          <circle cx="85" cy="75" r="5" fill="#0a0a1a"/>
          <defs>
            <linearGradient id="ticketGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22d3ee"/>
              <stop offset="100%" stopColor="#8b5cf6"/>
            </linearGradient>
          </defs>
        </svg>
        
        <svg className="absolute top-[18%] right-[6%] w-28 h-18 opacity-15 transform rotate-15" viewBox="0 0 120 80" fill="none">
          <rect x="5" y="5" width="110" height="70" rx="8" stroke="#8b5cf6" strokeWidth="2"/>
          <line x1="85" y1="5" x2="85" y2="75" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="4 4"/>
        </svg>
        
        <svg className="absolute bottom-[15%] left-[8%] w-24 h-16 opacity-12 transform rotate-8" viewBox="0 0 120 80" fill="none">
          <rect x="5" y="5" width="110" height="70" rx="8" stroke="#22d3ee" strokeWidth="2"/>
          <line x1="85" y1="5" x2="85" y2="75" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="4 4"/>
        </svg>
        
        <svg className="absolute bottom-[20%] right-[10%] w-36 h-22 opacity-18 transform -rotate-10" viewBox="0 0 120 80" fill="none">
          <rect x="5" y="5" width="110" height="70" rx="8" stroke="url(#ticketGrad2)" strokeWidth="2"/>
          <line x1="85" y1="5" x2="85" y2="75" stroke="url(#ticketGrad2)" strokeWidth="1.5" strokeDasharray="4 4"/>
          <defs>
            <linearGradient id="ticketGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8b5cf6"/>
              <stop offset="100%" stopColor="#22d3ee"/>
            </linearGradient>
          </defs>
        </svg>
        
        {/* Sports icons with glow - static */}
        <div className="absolute top-[30%] left-[3%] text-5xl opacity-10 transform -rotate-12">🏀</div>
        <div className="absolute top-[55%] right-[4%] text-4xl opacity-10 transform rotate-15">🏒</div>
        <div className="absolute bottom-[35%] left-[6%] text-4xl opacity-8 transform rotate-6">🎭</div>
        <div className="absolute top-[40%] right-[8%] text-5xl opacity-8 transform -rotate-8">🎵</div>
        <div className="absolute bottom-[25%] right-[25%] text-4xl opacity-10 transform rotate-12">⭐</div>
        <div className="absolute top-[22%] left-[20%] text-4xl opacity-8 transform -rotate-6">🏈</div>
        
        {/* Bottom arena glow */}
        <div className="absolute bottom-0 left-0 right-0 h-48" style={{
          background: 'linear-gradient(to top, rgba(34, 211, 238, 0.15) 0%, rgba(139, 92, 246, 0.08) 30%, transparent 100%)'
        }}></div>
        
        {/* Top vignette */}
        <div className="absolute top-0 left-0 right-0 h-32" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)'
        }}></div>
        
        {/* Side vignettes */}
        <div className="absolute top-0 bottom-0 left-0 w-32" style={{
          background: 'linear-gradient(to right, rgba(0,0,0,0.3) 0%, transparent 100%)'
        }}></div>
        <div className="absolute top-0 bottom-0 right-0 w-32" style={{
          background: 'linear-gradient(to left, rgba(0,0,0,0.3) 0%, transparent 100%)'
        }}></div>
      </div>

      {/* Logo */}
      <div className="mb-6 relative z-10">
        <img 
          src={LOGO_URL} 
          alt="TicketASaurus" 
          className="h-24 md:h-32 w-auto object-contain drop-shadow-lg"
        />
      </div>

      {/* Step 1: Login */}
      {step === 1 && (
        <Card className="w-full max-w-md shadow-2xl bg-gray-900/95 border-2 border-cyan-400/70 relative z-10"
          style={{
            boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1), inset 0 0 20px rgba(34, 211, 238, 0.05)'
          }}
        >
          <CardContent className="p-6 space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-white">Welcome Back</h2>
              <p className="text-gray-300 text-sm mt-1">Sign in to access your events</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-1">Email</label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>
              
              {/* Biometric Login Button - shown on mobile & tablets whenever
                  credentials have been set up (biometricEnabled already means
                  a platform credential exists for this account). */}
              {biometricEnabled && (isMobile || isTablet || isLargeTablet) && (
                <button 
                  type="button"
                  className="w-full h-12 text-base font-bold flex items-center justify-center gap-2 rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors shadow-md"
                  onClick={handleBiometricLogin}
                  disabled={isLoading}
                  data-testid="biometric-login-btn"
                >
                  👆 Login with Fingerprint
                </button>
              )}
              
              {/* Divider - only show when the biometric button is visible */}
              {biometricEnabled && (isMobile || isTablet || isLargeTablet) && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-gray-600"></div>
                  <span className="text-xs text-gray-400 font-medium">or use password</span>
                  <div className="flex-1 border-t border-gray-600"></div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-1">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10 bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-600 bg-gray-800"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-300 font-medium">
                  Remember my email
                </label>
              </div>
              
              {error && (
                <p className="text-sm text-red-400 text-center bg-red-900/50 p-2 rounded font-medium">{error}</p>
              )}
              
              <Button 
                type="submit" 
                className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
              
              {/* Lost Password Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowLostPasswordModal(true)}
                  className="text-sm text-gray-400 hover:text-blue-400 underline"
                >
                  Lost Password? Click here
                </button>
              </div>
            </form>
            
            {/* IP Address */}
            <p className="text-xs text-center text-gray-400 mt-4">
              Your IP: {ipAddress}
            </p>
            
            {/* Security Badges */}
            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center gap-1.5 text-xs text-gray-300">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>SSL Secure</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-300">
                <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>256-bit Encryption</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-300">
                <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
                <span>Privacy Protected</span>
              </div>
            </div>
            
            {/* Registration & Learn More — prominent side-by-side action buttons */}
            <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
              <p className="text-center text-sm text-gray-300 font-medium">
                Don't have an account yet?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setShowRegisterModal(true)}
                  data-testid="request-access-btn"
                  className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-bold text-sm shadow-lg hover:shadow-blue-500/50 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <span className="text-lg">📝</span>
                  <span>Request Access</span>
                </button>
                <a
                  href="/learn-more"
                  data-testid="want-to-learn-more-link"
                  className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-400 hover:to-teal-500 text-white font-bold text-sm shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 no-underline"
                >
                  <span className="text-lg">💡</span>
                  <span>Learn More</span>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={showRegisterModal} 
        onClose={() => setShowRegisterModal(false)} 
      />

      {/* Lost Password Modal */}
      <LostPasswordModal 
        isOpen={showLostPasswordModal} 
        onClose={() => setShowLostPasswordModal(false)} 
      />

      {/* Step 2: Event Selection */}
      {step === 2 && !showBiometricPrompt && (
        <Card className="w-full max-w-md shadow-2xl border-2 border-cyan-400/70"
          style={{
            boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1), inset 0 0 20px rgba(34, 211, 238, 0.05)'
          }}
        >
          <CardContent className="p-3 pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold leading-tight">Welcome, {loggedInUser?.name}!</h2>
                <p className="text-gray-400 text-[10px]">Select an event to continue</p>
              </div>
              <div className="flex items-center gap-1">
                {/* Tablet View Toggle - show for tablets and smaller desktops */}
                {isLargeTablet && (
                  <button
                    onClick={() => setViewOverride(viewOverride === 'desktop' ? 'mobile' : 'desktop')}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-500 transition-colors text-xs text-white"
                    title={viewOverride === 'desktop' ? 'Switch to Mobile View' : 'Switch to Desktop View'}
                  >
                    {viewOverride === 'desktop' ? '📱 Mobile' : '🖥️ Desktop'}
                  </button>
                )}
                <button
                  onClick={() => setShowWeatherPanel(true)}
                  data-testid="weather-panel-btn"
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-gray-700/50 hover:bg-gray-600/50 transition-colors text-[10px]"
                  title="Weather Forecast"
                >
                  {todayWeather ? (
                    <span className="text-base leading-none">{getWeatherEmoji(todayWeather.label)}</span>
                  ) : (
                    <span>🌤</span>
                  )}
                  {todayWeather && <span className="text-amber-400 font-bold">{todayWeather.high}°</span>}
                </button>
                <button
                  onClick={() => setShowUserProfile(true)}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-700/50 hover:bg-gray-600/50 transition-colors text-[10px]"
                  title="User Profile"
                >
                  👤 <span className="text-gray-300">Profile</span>
                </button>
                <button
                  onClick={() => {
                    setShowFriendsFamily(true);
                  }}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-700/50 hover:bg-gray-600/50 transition-colors text-[10px]"
                  title="Friends & Family"
                >
                  👥 <span className="text-gray-300">F&F</span>
                </button>
              </div>
            </div>
            
            {/* Global Calendar Button - Only show if user has multiple events */}
            {userEvents.length > 1 && (
              <div className="flex gap-1.5 items-stretch">
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-1 px-2 rounded-lg border-2 border-purple-500 bg-purple-50 hover:bg-purple-100 transition-all flex items-center gap-2"
                >
                  <span className="text-sm">📅</span>
                  <span className="font-bold text-purple-800 text-xs">Global Calendar</span>
                  <span className="text-[10px] text-purple-600">({userEvents.length} events)</span>
                </button>
                {hiddenEvents.length > 0 && (
                  <button
                    onClick={() => setShowHiddenEvents(!showHiddenEvents)}
                    className={`py-1 px-2 rounded-lg border-2 transition-all flex items-center gap-1 ${
                      showHiddenEvents 
                        ? 'border-amber-500 bg-amber-50 hover:bg-amber-100' 
                        : 'border-gray-400 bg-gray-50 hover:bg-gray-100'
                    }`}
                    title={showHiddenEvents ? 'Hide archived events' : 'Show archived events'}
                  >
                    <span className="text-sm">{showHiddenEvents ? '👁️' : '📦'}</span>
                    <span className={`font-bold text-[10px] ${showHiddenEvents ? 'text-amber-800' : 'text-gray-700'}`}>{hiddenEvents.length}</span>
                  </button>
                )}
              </div>
            )}
            
            {/* Single event with hidden events button */}
            {userEvents.length === 1 && hiddenEvents.length > 0 && (
              <button
                onClick={() => setShowHiddenEvents(!showHiddenEvents)}
                className={`w-full p-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                  showHiddenEvents 
                    ? 'border-amber-500 bg-amber-50 hover:bg-amber-100' 
                    : 'border-gray-400 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">{showHiddenEvents ? '👁️' : '📦'}</span>
                <div className="text-left">
                  <div className={`font-bold text-sm ${showHiddenEvents ? 'text-amber-800' : 'text-gray-700'}`}>
                    {showHiddenEvents ? 'Hide Archived Events' : 'Archived Events'}
                  </div>
                  <div className={`text-xs ${showHiddenEvents ? 'text-amber-600' : 'text-gray-500'}`}>
                    {hiddenEvents.length} hidden event{hiddenEvents.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </button>
            )}
            
            {/* Active Events */}
            <div className="space-y-1.5">
              {[...visibleEvents].sort((a, b) => a.teamName.localeCompare(b.teamName)).map((event, idx) => (
                <div
                  key={event.id || idx}
                  className="w-full p-1.5 rounded-lg border-2 transition-all hover:shadow-md flex items-center gap-2 bg-white"
                  style={{
                    borderColor: event.primaryColor || '#0053BC'
                  }}
                >
                  <button
                    onClick={() => handleSelectEvent(event)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    {event.sport?.toLowerCase() === 'family' ? (
                      <span className="w-7 h-7 flex items-center justify-center text-2xl">🕐</span>
                    ) : (
                      <img 
                        src={getTeamLogo(event)} 
                        alt={event.teamName}
                        className="w-7 h-7 object-contain"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900 text-sm leading-tight truncate">{event.teamName}</div>
                      <div className="text-[10px] text-gray-600">
                        {getSportEmoji(event.sport)} {event.season}
                      </div>
                    </div>
                    {(() => {
                      const sport = (event.sport || '').toLowerCase();
                      if (sport === 'family' || sport === 'concerts') return null;
                      const rec = seasonRecords[event.id];
                      if (!rec) return null;
                      const w = rec.wins || 0;
                      const l = rec.losses || 0;
                      const ot = rec.ot_losses;
                      if (w === 0 && l === 0) return null;
                      const label = (sport === 'hockey' && typeof ot === 'number') ? `${w}-${l}-${ot}` : `${w}-${l}`;
                      return (
                        <span
                          data-testid={`event-record-${event.id}`}
                          className="px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white tracking-tight shrink-0"
                          style={{ backgroundColor: event.primaryColor || '#0053BC' }}
                          title={`Season record: ${label}`}
                        >
                          {label}
                        </span>
                      );
                    })()}
                    <span className="text-lg text-gray-400">→</span>
                  </button>
                  <button
                    onClick={(e) => handleToggleEventHidden(event, e)}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Hide this event"
                  >
                    👁️
                  </button>
                </div>
              ))}
            </div>
            
            {/* Hidden/Archived Events Section */}
            {showHiddenEvents && hiddenEvents.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <span>📦</span> Archived Events
                </div>
                <div className="space-y-2">
                  {[...hiddenEvents].sort((a, b) => a.teamName.localeCompare(b.teamName)).map((event, idx) => (
                    <div
                      key={event.id || idx}
                      className="w-full p-2.5 rounded-lg border-2 border-dashed transition-all hover:shadow-md flex items-center gap-3 bg-gray-50 opacity-75"
                      style={{
                        borderColor: event.primaryColor || '#999'
                      }}
                    >
                      <button
                        onClick={() => handleSelectEvent(event)}
                        className="flex items-center gap-3 flex-1 text-left"
                      >
                        {event.sport?.toLowerCase() === 'family' ? (
                          <span className="w-9 h-9 flex items-center justify-center text-3xl opacity-60">🕐</span>
                        ) : (
                          <img 
                            src={getTeamLogo(event)} 
                            alt={event.teamName}
                            className="w-9 h-9 object-contain opacity-60"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-bold text-gray-600 text-base">{event.teamName}</div>
                          <div className="text-xs text-gray-500">
                            {getSportEmoji(event.sport)} {event.season}
                          </div>
                        </div>
                        {(() => {
                          const sport = (event.sport || '').toLowerCase();
                          if (sport === 'family' || sport === 'concerts') return null;
                          const rec = seasonRecords[event.id];
                          if (!rec) return null;
                          const w = rec.wins || 0;
                          const l = rec.losses || 0;
                          const ot = rec.ot_losses;
                          if (w === 0 && l === 0) return null;
                          const label = (sport === 'hockey' && typeof ot === 'number') ? `${w}-${l}-${ot}` : `${w}-${l}`;
                          return (
                            <span
                              className="px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white tracking-tight shrink-0 opacity-70"
                              style={{ backgroundColor: event.primaryColor || '#777' }}
                              title={`Season record: ${label}`}
                            >
                              {label}
                            </span>
                          );
                        })()}
                        <span className="text-xl text-gray-300">→</span>
                      </button>
                      <button
                        onClick={(e) => handleToggleEventHidden(event, e)}
                        className="p-1.5 rounded-md hover:bg-gray-200 text-gray-400 hover:text-green-600 transition-colors"
                        title="Unhide this event"
                      >
                        👁️‍🗨️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <Button 
              type="button"
              variant="outline" 
              className="w-full h-8 mt-2 text-red-600 border-red-300 hover:bg-red-50 text-sm"
              onClick={handleFullSignOut}
            >
              🚪 Sign Out
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Biometric Setup Prompt (after login, before event selection) */}
      {step === 2 && showBiometricPrompt && (
        <Card className="w-full max-w-md shadow-2xl bg-white border-2 border-cyan-400/70"
          style={{
            boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1), inset 0 0 20px rgba(34, 211, 238, 0.05)'
          }}
        >
          <CardContent className="p-6 space-y-4">
            <div className="text-center mb-4">
              <div className="text-5xl mb-4">🔐</div>
              <h2 className="text-2xl font-bold text-gray-900">Enable Quick Login?</h2>
              <p className="text-gray-600 text-sm mt-2">
                Hi {loggedInUser?.name}! Use Face ID, Touch ID, or fingerprint to quickly log in next time without entering your password.
              </p>
            </div>
            
            <div className="space-y-3">
              <button 
                onClick={setupBiometric}
                className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md transition-colors text-lg flex items-center justify-center gap-2"
              >
                <span>🔐</span> Yes, Enable Quick Login
              </button>
              <button 
                onClick={skipBiometric}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md border border-gray-300 transition-colors"
              >
                Maybe Later
              </button>
              <button 
                onClick={neverAskBiometric}
                className="w-full py-2 px-4 text-red-500 hover:text-red-700 text-sm font-medium transition-colors"
              >
                ✕ Never Ask Again
              </button>
            </div>
            
            <p className="text-xs text-center text-gray-400 mt-4">
              You can change this later in User Profile → Security
            </p>
          </CardContent>
        </Card>
      )}

      {/* User Profile Modal */}
      <Dialog open={showUserProfile} onOpenChange={setShowUserProfile}>
        <DialogContent className="sm:max-w-md border-2 border-cyan-400/70"
          style={{
            boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl">User Profile</DialogTitle>
            <DialogDescription>
              Manage your account settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Info */}
            <div className="space-y-2">
              <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-600">Name</span>
                <span className="font-medium text-gray-900">{loggedInUser?.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-600">Email</span>
                <span className="font-medium text-sm text-gray-900">{loggedInUser?.email || 'N/A'}</span>
              </div>
              {loggedInUser?.partner_id && (
                <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                  <span className="text-sm text-gray-600">Partner ID</span>
                  <span className="font-bold text-blue-600">{loggedInUser.partner_id}</span>
                </div>
              )}
            </div>

            {/* Change Password Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Change Password</h4>
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    type={showProfilePassword ? "text" : "password"}
                    placeholder="New Password"
                    value={profilePassword}
                    onChange={(e) => setProfilePassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowProfilePassword(!showProfilePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showProfilePassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <div className="relative">
                  <Input
                    type={showProfilePassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={profileConfirmPassword}
                    onChange={(e) => setProfileConfirmPassword(e.target.value)}
                    className={`pr-10 ${profilePassword && profileConfirmPassword && profilePassword !== profileConfirmPassword ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowProfilePassword(!showProfilePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showProfilePassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {profilePassword && profileConfirmPassword && profilePassword !== profileConfirmPassword && (
                  <p className="text-red-500 text-xs">Passwords do not match</p>
                )}
                <Button
                  onClick={async () => {
                    if (!profilePassword) {
                      setProfileMessage('❌ Please enter a new password');
                      return;
                    }
                    if (profilePassword !== profileConfirmPassword) {
                      setProfileMessage('❌ Passwords do not match');
                      return;
                    }
                    try {
                      await axios.put(`${API}/users/${loggedInUser?.email}`, {
                        password: profilePassword
                      });
                      setProfileMessage('✅ Password updated successfully');
                      setProfilePassword('');
                      setProfileConfirmPassword('');
                      setTimeout(() => setProfileMessage(''), 3000);
                    } catch (err) {
                      setProfileMessage('❌ Failed to update password');
                    }
                  }}
                  className="w-full"
                  disabled={!profilePassword || !profileConfirmPassword || profilePassword !== profileConfirmPassword}
                >
                  Update Password
                </Button>
                {profileMessage && (
                  <p className={`text-sm ${profileMessage.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                    {profileMessage}
                  </p>
                )}
              </div>
            </div>

            {/* Reset Biometric */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Biometric Login</h4>
              <Button
                variant="outline"
                onClick={() => {
                  if (loggedInUser?.email) {
                    const biometricData = localStorage.getItem('biometric_data');
                    if (biometricData) {
                      const data = JSON.parse(biometricData);
                      if (data[loggedInUser.email]) {
                        delete data[loggedInUser.email];
                        localStorage.setItem('biometric_data', JSON.stringify(data));
                      }
                    }
                    setBiometricEnabled(false);
                    setProfileMessage('✅ Biometric login reset. You can set it up again on next login.');
                    setTimeout(() => setProfileMessage(''), 3000);
                  }
                }}
                className="w-full"
              >
                🔄 Reset Biometric Login
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <FriendsFamilyPanel
        open={showFriendsFamily}
        onOpenChange={setShowFriendsFamily}
        userEmail={loggedInUser?.email || loggedInUser?.id}
      />

      {/* Weather Panel */}
      <Dialog open={showWeatherPanel} onOpenChange={setShowWeatherPanel}>
        <DialogContent 
          className="max-w-sm mx-auto max-h-[85vh] overflow-y-auto overflow-x-hidden"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            border: '1px solid rgba(56, 189, 248, 0.3)',
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2 text-white">
              {todayWeather && <span className="text-2xl leading-none">{getWeatherEmoji(todayWeather.label)}</span>}
              Dallas Weather
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {weatherRadarView ? 'Live radar' : '14-day forecast'}
            </DialogDescription>
          </DialogHeader>

          {/* Forecast / Radar toggle */}
          <div className="flex rounded-lg overflow-hidden border border-gray-600 mb-2">
            <button
              data-testid="weather-tab-forecast"
              onClick={() => setWeatherRadarView(false)}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold transition-colors ${
                !weatherRadarView ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Forecast
            </button>
            <button
              data-testid="weather-tab-radar"
              onClick={() => setWeatherRadarView(true)}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold transition-colors ${
                weatherRadarView ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Radar / Satellite
            </button>
          </div>

          {weatherRadarView ? (
            /* Radar View */
            <div className="rounded-lg overflow-hidden border border-gray-700 relative" style={{ height: 400 }}>
              <iframe
                key={radarKey}
                title="Dallas Weather Radar"
                src={`https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=in&metricTemp=%C2%B0F&metricWind=mph&zoom=9&overlay=satellite&product=satellite&level=surface&lat=${userCoords?.lat || '32.78'}&lon=${userCoords?.lon || '-96.80'}&message=true`}
                frameBorder="0"
                className="w-full h-full"
                loading="lazy"
              />
              <button
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setUserCoords({ lat: pos.coords.latitude.toFixed(2), lon: pos.coords.longitude.toFixed(2) });
                        setRadarKey(k => k + 1);
                      },
                      () => setRadarKey(k => k + 1),
                      { timeout: 5000 }
                    );
                  } else {
                    setRadarKey(k => k + 1);
                  }
                }}
                className="absolute bottom-14 right-2 z-10 bg-gray-900/90 hover:bg-gray-800 text-white text-xs font-semibold px-3 py-2 rounded-lg border border-gray-600 shadow-lg flex items-center gap-1.5"
                data-testid="recenter-map-btn"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/></svg>
                Re-center
              </button>
            </div>
          ) : (
            /* Forecast View */
            <>
              {/* Today's highlight */}
              {todayWeather && (
                <div className="rounded-xl p-4 mb-2" style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-cyan-400 font-semibold uppercase tracking-wider">Now</div>
                      <div className="text-3xl font-bold text-white mt-1">
                        {todayWeather.current_temp || todayWeather.high}°
                        <span className="text-lg text-gray-400"> H:{todayWeather.high}° L:{todayWeather.low}°</span>
                      </div>
                      <div className="text-sm text-gray-300 mt-1">{todayWeather.label}</div>
                      {todayWeather.chance_rain > 0 && (
                        <div className="text-xs text-blue-400 mt-0.5">{todayWeather.chance_rain}% chance of rain today</div>
                      )}
                    </div>
                    <span className="text-5xl leading-none">{getWeatherEmoji(todayWeather.label)}</span>
                  </div>
                </div>
              )}

              {/* Daily forecast list */}
              <div className="space-y-1">
                {weatherDays.map(([dateStr, day]) => {
                  const d = new Date(dateStr + 'T12:00:00');
                  const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const now = new Date();
                  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                  const isToday = dateStr === todayStr;
                  
                  return (
                    <div
                      key={dateStr}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isToday ? 'bg-cyan-900/30 border border-cyan-700/40' : 'hover:bg-white/5'}`}
                    >
                      <div className="w-16 flex-shrink-0">
                        <div className={`text-xs font-bold ${isToday ? 'text-cyan-400' : 'text-gray-300'}`}>{isToday ? 'Today' : dayName}</div>
                        <div className="text-[10px] text-gray-500">{monthDay}</div>
                      </div>
                      <span className="text-2xl leading-none flex-shrink-0">{getWeatherEmoji(day.label)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-300 truncate">{day.label}</div>
                        {day.chance_rain > 0 && (
                          <div className="text-[10px] text-blue-400">{day.chance_rain}%</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-sm font-bold text-amber-400">{day.high}°</span>
                        <span className="text-sm text-gray-500 ml-1">{day.low}°</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <p className="text-sm text-gray-400 mt-6 text-center">
        {footerText} <span className="opacity-60">• {APP_VERSION}</span>
      </p>
    </div>
  );
};

export default CoverPage;
